import { useState, useEffect, useCallback } from "react";
import { ISales, IWithdrawalInput } from "src/domains";
import {
  queryGetSales,
  mutationWithdrawal,
  queryGetWithdrawalRequest,
} from "src/graphql";
import { withdrawalEmit, WithdrawalEvent } from "src/utils/event";
import { EventRegister } from "react-native-event-listeners";

export const useSalesHistory = () => {
  const [items, setItems] = useState<ISales[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<Error | undefined>();
  let _isMount = true;

  const refresh = useCallback(async () => {
    if (!_isMount) {
      return;
    }
    if (_isMount) {
      setLoading(true);
    }
    if (_isMount) {
      setError(undefined);
    }
    try {
      const response = await queryGetSales();
      if (_isMount) {
        setItems(response.data.getSales?.items || []);
      }
    } catch (e) {
      console.error(e);
      if (_isMount) {
        setError(e as Error);
      }
    }
    if (_isMount) {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const withdrawalListener = EventRegister.addEventListener(
      WithdrawalEvent,
      () => refresh()
    );

    return () => {
      EventRegister.removeEventListener(withdrawalListener as string);
    };
  }, [refresh]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  useEffect(() => {
    _isMount = true;
    return () => {
      _isMount = false;
    };
  }, []);

  return [items, loading, error, refresh] as const;
};

export const useSales = (id?: string) => {
  const [item, setItem] = useState<ISales | undefined>();
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<Error | undefined>();
  let _isMount = true;

  const refresh = useCallback(async () => {
    if (!_isMount || !id) {
      return;
    }
    if (_isMount) {
      setLoading(true);
    }
    if (_isMount) {
      setError(undefined);
    }
    try {
      const response = await queryGetWithdrawalRequest({ id });
      if (_isMount) {
        setItem(response.data.getWithdrawalRequest);
      }
    } catch (e) {
      console.error(e);
      if (_isMount) {
        setError(e as Error);
      }
    }
    if (_isMount) {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    refresh();
  }, [refresh, id]);

  useEffect(() => {
    _isMount = true;
    return () => {
      _isMount = false;
    };
  }, []);

  return [item, loading, error, refresh] as const;
};

export const useWithdrawalAction = () => {
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<Error | undefined>();

  const handler = useCallback(async (input: IWithdrawalInput) => {
    setLoading(true);
    setError(undefined);
    try {
      const response = await mutationWithdrawal({ input });
      withdrawalEmit();
      setLoading(false);
      return response.data.withdrawal || false;
    } catch (e) {
      console.error(e);
      setError(e as Error);
      setLoading(false);
      return false;
    }
  }, []);

  return [handler, loading, error] as const;
};
